import {createContext, Dispatch, SetStateAction, useEffect, useState} from 'react'
import qs from 'qs'
import {ID, QueryResponseContextProps, QueryState} from './models'

function createResponseContext<T>(initialState: QueryResponseContextProps<T>) {
  return createContext(initialState)
}

function isNotEmpty(obj: unknown) {
  return obj !== undefined && obj !== null && obj !== ''
}

// Example: page=1&items_per_page=10&sort=id&order=desc&search=a&filter_name=a&filter_online=false
function stringifyRequestQuery(state: QueryState): string {
  const pagination = qs.stringify(state, {filter: ['page', 'items_per_page'], skipNulls: true})
  const sort = qs.stringify(state, {filter: ['sort', 'order'], skipNulls: true})
  let type = undefined
  if (state.hasOwnProperty('type')) {
    type = qs.stringify(state, {filter: ['type'], skipNulls: true})
  }

  //start end date filter format
  let start_date = undefined
  if (state.hasOwnProperty('start_date')) {
    start_date = qs.stringify(state, {filter: ['start_date'], skipNulls: true})
  }
  let end_date = undefined
  if (state.hasOwnProperty('end_date')) {
    end_date = qs.stringify(state, {filter: ['end_date'], skipNulls: true})
  }
  let actual_inspection_date = undefined
  if (state.hasOwnProperty('actual_inspection_date')) {
    actual_inspection_date = qs.stringify(state, {filter: ['actual_inspection_date'], skipNulls: true})
  }
  let rescheduled_inspection_date = undefined
  if (state.hasOwnProperty('rescheduled_inspection_date')) {
    rescheduled_inspection_date = qs.stringify(state, {filter: ['rescheduled_inspection_date'], skipNulls: true})
  }
  //inspection_status filter search
  let inspection_status = undefined
  if (state.hasOwnProperty('inspection_status')) {
    inspection_status = qs.stringify(state, {filter: ['inspection_status'], skipNulls: true});
  }

  //company_id filter search
  let company_id = undefined
  if (state.hasOwnProperty('company_id')) {
    company_id = qs.stringify(state, {filter: ['company_id'], skipNulls: true});
  }

  //group_id filter search
  let group_id = undefined
  if (state.hasOwnProperty('group_id')) {
    group_id = qs.stringify(state, {filter: ['group_id'], skipNulls: true});
  }
  //plan_type filter search
  let plan_type = undefined
  if (state.hasOwnProperty('plan_type')) {
    plan_type = qs.stringify(state, {filter: ['plan_type'], skipNulls: true});
  }
  //is_Active filter search
  let is_active = undefined
  if (state.hasOwnProperty('is_active')) {
    is_active = qs.stringify(state, {filter: ['is_active'], skipNulls: true});
  }
  //team_size_id filter search
  let team_size_id = undefined
  if (state.hasOwnProperty('team_size_id')) {
    team_size_id = qs.stringify(state, {filter: ['team_size_id'], skipNulls: true});
  }

  //country filter search
  let country = undefined
  if (state.hasOwnProperty('country')) {
    country = qs.stringify(state, {filter: ['country'], skipNulls: true});
  }
  //status filter search
  let status = undefined
  if (state.hasOwnProperty('status')) {
    status = qs.stringify(state, {filter: ['status'], skipNulls: true});
  }
  //subscription_id filter search
  let subscription_id = undefined
  if (state.hasOwnProperty('subscription_id')) {
    subscription_id = qs.stringify(state, {filter: ['subscription_id'], skipNulls: true});
  }
  //category filter search
  let category_ids = undefined
  if (state.hasOwnProperty('category_ids') && Array.isArray(state.category_ids)) {
    category_ids = state.category_ids.join(',')
    category_ids = state.category_ids?.every(item => item !== '') ? qs.stringify({category_ids}) : undefined;
  }

   //template_plan_type filter search
   let template_plan_type = undefined
   if (state.hasOwnProperty('template_plan_type')) {
    template_plan_type = qs.stringify(state, {filter: ['template_plan_type'], skipNulls: true});
  }

  //global activity search
  let template_type = undefined
  if (state.hasOwnProperty('template_type')) {
    template_type = qs.stringify(state, {filter: ['template_type'], skipNulls: true});
  }

  //is_mapping filter search
  let is_mapping = undefined
  if (state.hasOwnProperty('is_mapping')) {
    is_mapping = qs.stringify(state, {filter: ['is_mapping'], skipNulls: true});
  }

  // log_type filter 
  let log_type = undefined
  if (state.hasOwnProperty('log_type')) {
    log_type = qs.stringify(state, {filter: ['log_type'], skipNulls: true});
  }
  
  //remove_template_id
  let remove_template_id = undefined
  if (state.hasOwnProperty('remove_template_id')) {
    remove_template_id = qs.stringify(state, {filter: ['remove_template_id'], skipNulls: true});
  }

  const search = isNotEmpty(state.search)
    ? qs.stringify(state, {filter: ['search'], skipNulls: true})
    : ''

  const filter = state.filter
    ? Object.entries(state.filter as Object)
        .filter((obj) => isNotEmpty(obj[1]))
        .map((obj) => {
          return `filter_${obj[0]}=${obj[1]}`
        })
        .join('&')
    : ''

  return [
    pagination,
    sort,
    search,
    filter,
    type,
    start_date,
    end_date,
    actual_inspection_date,
    rescheduled_inspection_date,
    inspection_status,
    company_id,
    group_id,
    plan_type,
    is_active,
    team_size_id,
    status,
    subscription_id,
    country,
    category_ids,
    template_type,
    is_mapping,
    log_type,
    remove_template_id,
    template_plan_type
  ]
    .filter((f) => f)
    .join('&')
  // .toLowerCase()
}

//without pagination export data response

function stringifyRequestQueryForEveryKey(state: any): string {
  const queryParams = Object.entries(state)
    .filter(([key, value]) => value)
    .map(([key, value]) => qs.stringify({[key]: value}, {skipNulls: true}))
    .join('&')
  return queryParams
}

function parseRequestQuery(query: string): QueryState {
  const cache: unknown = qs.parse(query)
  return cache as QueryState
}

function calculatedGroupingIsDisabled<T>(isLoading: boolean, data: Array<T> | undefined): boolean {
  if (isLoading) {
    return true
  }

  return !data || !data.length
}

function calculateIsAllDataSelected<T>(data: Array<T> | undefined, selected: Array<ID>): boolean {
  if (!data) {
    return false
  }

  return data.length > 0 && data.length === selected.length
}

function groupingOnSelect(
  id: ID,
  selected: Array<ID>,
  setSelected: Dispatch<SetStateAction<Array<ID>>>
) {
  if (!id) {
    return
  }

  if (selected.includes(id)) {
    setSelected(selected.filter((itemId) => itemId !== id))
  } else {
    const updatedSelected = [...selected]
    updatedSelected.push(id)
    setSelected(updatedSelected)
  }
}

function groupingOnSelectAll<T>(
  isAllSelected: boolean,
  setSelected: Dispatch<SetStateAction<Array<ID>>>,
  data?: Array<T & {id?: ID}>
) {
  if (isAllSelected) {
    setSelected([])
    return
  }

  if (!data || !data.length) {
    return
  }

  setSelected(data.filter((item) => item.id).map((item) => item.id))
}

// Hook
function useDebounce(value: string | undefined, delay: number) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler)
      }
    },
    [value, delay] // Only re-call effect if value or delay changes
  )
  return debouncedValue
}

function getFilterBadgeCount(state: any): number {
  const data = Object.keys(state).reduce((p: string[], key: string) => {
    if (
      key === 'page' ||
      key === 'sort' ||
      key === 'order' ||
      (key === 'company_id' && Object.keys(state).includes('inspection_status'))
    )
      return p
    if (
      !state[key] ||
      state[key] === 'all' ||
      state[key] === 'all' ||
      state[key] === '' ||
      state[key] === ' ' ||
      state[key] === 'Archive' ||
      (Array.isArray(state[key])  && state[key].length === 1 && state[key][0] === '')
    )
      return p
    return [...p, key]
  }, [])
  return data.length
}

export {
  createResponseContext,
  stringifyRequestQuery,
  stringifyRequestQueryForEveryKey,
  parseRequestQuery,
  calculatedGroupingIsDisabled,
  calculateIsAllDataSelected,
  groupingOnSelect,
  groupingOnSelectAll,
  useDebounce,
  isNotEmpty,
  getFilterBadgeCount,
}
